import { useState, useRef, useCallback, useMemo } from 'react';
import {
  useImportBizcard,
  useConfirmBizcard,
  usePartnerCategories,
  useBizcardPartners,
} from '@saiens/shared/hooks';
import { toast } from '@saiens/shared/stores';
import { compressImage } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Camera,
  ImagePlus,
  Upload,
  Check,
  User,
  Building,
  Phone,
  X,
  Pencil,
  ArrowLeft,
  Search,
  Tag,
} from 'lucide-react';
import type {
  BizcardImportResult,
  BizcardOcrResult,
  PartnerCategory,
} from '@saiens/shared/types';

type PageStep = 'select' | 'preview' | 'success';
type ImageSide = 'front' | 'back';

const OCR_FIELD_LABELS: Record<keyof BizcardOcrResult, string> = {
  name: '姓名',
  company: '公司',
  title: '職稱',
  department: '部門',
  phone: '電話',
  mobile: '手機',
  fax: '傳真',
  email: 'Email',
  website: '網站',
  address: '地址',
  zip: '郵遞區號',
  vat: '統編',
};

/**
 * Business Card Import Page - Two-step flow
 *
 * 1. Select/capture front image (required) + back image (optional) -> OCR scan
 * 2. Preview & edit OCR results -> Confirm import
 */
export function BizcardImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<PageStep>('select');
  const [frontImagePreview, setFrontImagePreview] = useState<string | null>(null);
  const [frontImageBase64, setFrontImageBase64] = useState<string | null>(null);
  const [backImagePreview, setBackImagePreview] = useState<string | null>(null);
  const [backImageBase64, setBackImageBase64] = useState<string | null>(null);
  const activeInputRef = useRef<ImageSide>('front');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [ocrResult, setOcrResult] = useState<BizcardOcrResult | null>(null);
  const [confirmResult, setConfirmResult] = useState<BizcardImportResult | null>(null);
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  const { data: categories } = usePartnerCategories();
  const { data: recentPartners, isLoading: partnersLoading } = useBizcardPartners(10);
  const importBizcard = useImportBizcard();
  const confirmBizcard = useConfirmBizcard();

  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    return categories
      .filter((c: PartnerCategory) =>
        c.name.toLowerCase().includes(categorySearch.toLowerCase())
      )
      .sort((a: PartnerCategory, b: PartnerCategory) => {
        const aSelected = selectedCategories.includes(a.id);
        const bSelected = selectedCategories.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
  }, [categories, categorySearch, selectedCategories]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('檔案格式錯誤', '請選擇圖片檔案');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('檔案過大', '圖片大小不能超過 10MB');
      return;
    }

    // Capture current side from ref (avoids async state race condition)
    const side = activeInputRef.current;

    try {
      const { base64, previewUrl } = await compressImage(file);

      if (side === 'front') {
        setFrontImagePreview(previewUrl);
        setFrontImageBase64(base64);
        setOcrResult(null);
        setConfirmResult(null);
      } else {
        setBackImagePreview(previewUrl);
        setBackImageBase64(base64);
      }
    } catch {
      toast.error('處理失敗', '無法處理圖片');
    }

    e.target.value = '';
  }, []);

  const handleCameraCapture = (side: ImageSide) => {
    activeInputRef.current = side;
    cameraInputRef.current?.click();
  };

  const handleGallerySelect = (side: ImageSide) => {
    activeInputRef.current = side;
    fileInputRef.current?.click();
  };

  const handleClearFrontImage = () => {
    setFrontImagePreview(null);
    setFrontImageBase64(null);
    setOcrResult(null);
    setStep('select');
  };

  const handleClearBackImage = () => {
    setBackImagePreview(null);
    setBackImageBase64(null);
  };

  const toggleCategory = (id: number) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleStartOcr = () => {
    if (!frontImageBase64) return;

    importBizcard.mutate(
      {
        image_base64: frontImageBase64,
        back_image_base64: backImageBase64 || undefined,
      },
      {
        onSuccess: (data) => {
          setOcrResult(data.ocr_result);
          setStep('preview');
        },
      }
    );
  };

  const handleOcrFieldChange = (field: keyof BizcardOcrResult, value: string) => {
    if (!ocrResult) return;
    setOcrResult({ ...ocrResult, [field]: value });
  };

  const handleConfirmImport = () => {
    if (!ocrResult || !frontImageBase64) return;

    confirmBizcard.mutate(
      {
        ocr_result: ocrResult,
        image_base64: frontImageBase64,
        back_image_base64: backImageBase64 || undefined,
        category_ids: selectedCategories.length > 0 ? selectedCategories : undefined,
      },
      {
        onSuccess: (data) => {
          setConfirmResult(data);
          setStep('success');
        },
      }
    );
  };

  const handleBackToSelect = () => {
    setOcrResult(null);
    setStep('select');
  };

  const handleNewScan = () => {
    setFrontImagePreview(null);
    setFrontImageBase64(null);
    setBackImagePreview(null);
    setBackImageBase64(null);
    setOcrResult(null);
    setConfirmResult(null);
    setSelectedCategories([]);
    setStep('select');
  };

  const hasFrontImage = Boolean(frontImagePreview);

  return (
    <div className="pb-24 px-4 py-6">
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Camera capture"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Gallery select"
      />

      {/* Header */}
      <div className="mb-6">
        {step === 'preview' && (
          <button
            onClick={handleBackToSelect}
            className="mb-2 flex items-center gap-1 text-sm text-gray-500"
          >
            <ArrowLeft className="h-4 w-4" />
            返回
          </button>
        )}
        <h1 className="text-xl font-bold text-gray-900">名片匯入</h1>
        <p className="mt-1 text-sm text-gray-500">
          {step === 'select' && '拍照或上傳名片，自動識別聯絡人資訊'}
          {step === 'preview' && '確認識別結果，可直接編輯修正'}
          {step === 'success' && '匯入完成'}
        </p>
      </div>

      {/* === Step: Success === */}
      {step === 'success' && confirmResult && (
        <div className="mb-6 rounded-xl bg-green-50 p-4">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            <span className="font-medium">
              {confirmResult.is_new ? '已新增聯絡人' : '已更新聯絡人'}
            </span>
          </div>
          <div className="mt-3 rounded-lg bg-white p-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  {confirmResult.partner_name}
                </h3>
                {confirmResult.company_name && (
                  <p className="text-sm text-gray-500">{confirmResult.company_name}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                  {confirmResult.partner.function && (
                    <span className="rounded bg-gray-100 px-2 py-0.5">
                      {confirmResult.partner.function}
                    </span>
                  )}
                  {confirmResult.partner.mobile && (
                    <span className="rounded bg-gray-100 px-2 py-0.5">
                      {confirmResult.partner.mobile}
                    </span>
                  )}
                  {confirmResult.partner.email && (
                    <span className="rounded bg-gray-100 px-2 py-0.5">
                      {confirmResult.partner.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={handleNewScan}
            className="mt-4 w-full rounded-lg border border-green-600 py-2 text-center text-sm font-medium text-green-600"
          >
            繼續掃描新名片
          </button>
        </div>
      )}

      {/* === Step: OCR Preview & Edit === */}
      {step === 'preview' && ocrResult && (
        <div className="mb-6">
          {/* Image thumbnails (front + back) */}
          <div className="mb-4 flex gap-2">
            {frontImagePreview && (
              <div className="flex-1 rounded-lg bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs text-gray-500">正面</p>
                <img
                  src={frontImagePreview}
                  alt="名片正面"
                  className="mx-auto rounded-lg object-contain"
                  style={{ maxHeight: '120px' }}
                />
              </div>
            )}
            {backImagePreview && (
              <div className="flex-1 rounded-lg bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs text-gray-500">反面</p>
                <img
                  src={backImagePreview}
                  alt="名片反面"
                  className="mx-auto rounded-lg object-contain"
                  style={{ maxHeight: '120px' }}
                />
              </div>
            )}
          </div>

          {/* Editable OCR fields */}
          <div className="rounded-xl bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <Pencil className="h-4 w-4 text-gray-500" />
              <h3 className="font-medium text-gray-900">識別結果</h3>
            </div>
            <div className="divide-y">
              {(Object.keys(OCR_FIELD_LABELS) as Array<keyof BizcardOcrResult>).map((field) => (
                <div key={field} className="flex items-center gap-3 px-4 py-2.5">
                  <label className="w-16 flex-shrink-0 text-xs text-gray-500">
                    {OCR_FIELD_LABELS[field]}
                  </label>
                  <input
                    type="text"
                    value={ocrResult[field]}
                    onChange={(e) => handleOcrFieldChange(field, e.target.value)}
                    className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={`輸入${OCR_FIELD_LABELS[field]}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          {categories && categories.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">選擇標籤</h3>
                <button
                  onClick={() => {
                    setCategorySearch('');
                    setShowCategorySheet(true);
                  }}
                  className="flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary"
                >
                  <Tag className="h-3.5 w-3.5" />
                  + 選擇
                </button>
              </div>
              {selectedCategories.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedCategories.map((catId) => {
                    const cat = categories.find((c: PartnerCategory) => c.id === catId);
                    if (!cat) return null;
                    return (
                      <span
                        key={cat.id}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                      >
                        {cat.name}
                        <button
                          onClick={() => toggleCategory(cat.id)}
                          className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20"
                          aria-label={`移除標籤 ${cat.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-400">點擊選擇標籤</p>
              )}
            </div>
          )}

          {/* Category Bottom Sheet */}
          {showCategorySheet && categories && (
            <div
              className="fixed inset-0 z-50 flex flex-col justify-end"
              role="dialog"
              aria-modal="true"
              aria-labelledby="category-sheet-title"
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowCategorySheet(false);
              }}
            >
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowCategorySheet(false)}
              />
              {/* Panel */}
              <div className="relative rounded-t-2xl bg-white pb-6">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <h3 id="category-sheet-title" className="text-base font-semibold text-gray-900">
                    選擇標籤
                  </h3>
                  <button
                    onClick={() => setShowCategorySheet(false)}
                    className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-white"
                  >
                    完成
                  </button>
                </div>
                {/* Search */}
                <div className="px-4 py-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      placeholder="搜尋標籤..."
                      className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
                {/* List */}
                <div className="max-h-[60vh] overflow-y-auto px-4">
                  {filteredCategories.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-400">
                      找不到符合的標籤
                    </p>
                  ) : (
                    filteredCategories.map((cat: PartnerCategory) => {
                      const isSelected = selectedCategories.includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          onClick={() => toggleCategory(cat.id)}
                          className="flex w-full items-center gap-3 border-b border-gray-100 py-3 text-left last:border-b-0"
                        >
                          <div
                            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                              isSelected
                                ? 'border-primary bg-primary text-white'
                                : 'border-gray-300 bg-white'
                            }`}
                            role="checkbox"
                            aria-checked={isSelected}
                          >
                            {isSelected && <Check className="h-3.5 w-3.5" />}
                          </div>
                          <span className="text-sm text-gray-800">{cat.name}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === Step: Select Image === */}
      {step === 'select' && (
        <>
          {/* Front image upload area (required) */}
          <div className="mb-4">
            {!frontImagePreview ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-6">
                <div className="flex flex-col items-center">
                  <ImagePlus className="h-10 w-10 text-gray-400" />
                  <p className="mt-2 text-center text-sm font-medium text-gray-700">
                    名片正面
                  </p>
                  <p className="mt-1 text-center text-xs text-gray-400">
                    拍攝或選擇名片圖片
                  </p>
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={() => handleCameraCapture('front')}
                      aria-label="拍攝名片正面"
                      className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
                    >
                      <Camera className="h-4 w-4" />
                      拍照
                    </button>
                    <button
                      onClick={() => handleGallerySelect('front')}
                      aria-label="從相簿選擇正面"
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                    >
                      <Upload className="h-4 w-4" />
                      相簿
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs font-medium text-gray-500">名片正面</p>
                <img
                  src={frontImagePreview}
                  alt="名片正面"
                  className="w-full rounded-lg object-contain"
                  style={{ maxHeight: '250px' }}
                />
                <button
                  onClick={handleClearFrontImage}
                  aria-label="清除正面圖片"
                  className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Back image upload area (optional) */}
          <div className="mb-6">
            {!backImagePreview ? (
              <div className="rounded-xl border-2 border-dashed border-gray-100 bg-gray-50/50 p-6">
                <div className="flex flex-col items-center">
                  <ImagePlus className="h-8 w-8 text-gray-300" />
                  <p className="mt-2 text-center text-sm font-medium text-gray-500">
                    名片反面（選填）
                  </p>
                  <p className="mt-1 text-center text-xs text-gray-400">
                    如有反面資訊可一併上傳
                  </p>
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={() => handleCameraCapture('back')}
                      aria-label="拍攝名片反面"
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      拍照
                    </button>
                    <button
                      onClick={() => handleGallerySelect('back')}
                      aria-label="從相簿選擇反面"
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      相簿
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs font-medium text-gray-500">名片反面</p>
                <img
                  src={backImagePreview}
                  alt="名片反面"
                  className="w-full rounded-lg object-contain"
                  style={{ maxHeight: '200px' }}
                />
                <button
                  onClick={handleClearBackImage}
                  aria-label="清除反面圖片"
                  className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Recent Imports */}
          {!hasFrontImage && (
            <div className="mb-6">
              <h2 className="mb-3 font-semibold text-gray-900">最近匯入</h2>
              {partnersLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : recentPartners && recentPartners.length > 0 ? (
                <div className="space-y-2">
                  {recentPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                        {partner.image_url ? (
                          <img
                            src={partner.image_url}
                            alt={partner.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-medium text-gray-900">{partner.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          {partner.company_name && (
                            <span className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {partner.company_name}
                            </span>
                          )}
                          {partner.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {partner.mobile}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg bg-gray-50 py-8 text-center text-sm text-gray-500">
                  尚無匯入記錄
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Bottom Action Buttons */}
      {step === 'select' && hasFrontImage && (
        <div className="fixed bottom-20 left-0 right-0 z-40 border-t bg-white p-4">
          <button
            onClick={handleStartOcr}
            disabled={importBizcard.isPending || !frontImageBase64}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            {importBizcard.isPending ? (
              <>
                <LoadingSpinner size="sm" />
                識別中...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                開始識別
              </>
            )}
          </button>
        </div>
      )}

      {step === 'preview' && !showCategorySheet && (
        <div className="fixed bottom-20 left-0 right-0 z-40 border-t bg-white p-4">
          <div className="flex gap-3">
            <button
              onClick={handleBackToSelect}
              disabled={confirmBizcard.isPending}
              className="flex-1 rounded-xl border border-gray-300 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={confirmBizcard.isPending || !ocrResult?.name}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
            >
              {confirmBizcard.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  匯入中...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  確認匯入
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
